"use client";

import type { Orientation } from "../types";
import { useCallback, useEffect, useRef, useState } from "react";
import { printCarePlanCanvas } from "@/lib/print-care-plan-canvas";
import { useToast } from "@/lib/use-toast";
export function useCarePlanPrintFlow({
  name,
  hasSteps
}: {
  name: string;
  hasSteps: boolean;
}) {
  const {
    toast
  } = useToast();
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const pendingPopup = useRef<Window | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingPopup.current?.close();
    };
  }, []);
  const handlePrint = useCallback(() => {
    if (!hasSteps) {
      toast({
        title: "Nothing to print",
        description: "This care plan has no steps.",
        variant: "error"
      });
      return;
    }
    setPrintDialogOpen(true);
  }, [hasSteps, toast]);
  const handlePrintConfirm = useCallback(async (_chosenDirection: Orientation, canvas: HTMLElement) => {
    if (pendingPopup.current) return;
    // Reserve synchronously inside the user gesture, before any async work.
    const popup = window.open("", "_blank");
    if (!popup) {
      toast({
        title: "Unable to print",
        description: "Allow popups to print the care-plan canvas.",
        variant: "error"
      });
      return;
    }
    popup.document.title = "Preparing care-plan canvas";
    popup.document.body.textContent = "Preparing the complete care-plan canvas…";
    pendingPopup.current = popup;
    setIsPrinting(true);
    try {
      // The chosen snapshot already captures graph direction. Paper settings
      // belong to the native print dialog, independently of this choice.
      await printCarePlanCanvas(canvas, popup, name);
      if (!mounted.current || popup.closed) return;
      popup.focus();
      popup.print();
      setPrintDialogOpen(false);
    } catch {
      popup.close();
      if (mounted.current) {
        toast({
          title: "Unable to print",
          description: "The care-plan canvas could not be prepared. Please try again.",
          variant: "error"
        });
      }
    } finally {
      pendingPopup.current = null;
      if (mounted.current) setIsPrinting(false);
    }
  }, [name, toast]);
  return {
    state: {
      dialogOpen: printDialogOpen,
      isPrinting
    },
    actions: {
      openDialog: handlePrint,
      onOpenChange: setPrintDialogOpen,
      confirmPrint: handlePrintConfirm
    }
  };
}